import { ServiceArea } from '../../types/serviceArea';
import {
	LAYER_ADM0_FILL,
	LAYER_ADM1_FILL,
	LAYER_ADM2_FILL,
	LAYER_ADM3_FILL,
	SEL_COUNTRY_LINE,
	SEL_DISTRICT_LINE,
	SEL_LOCALITY_LINE,
	SEL_REGION_LINE,
	SOURCE_ADM0,
	SOURCE_ADM1,
	SOURCE_ADM2,
} from './progressiveGeoBoundariesMapConstants';
import {
	boundaryFeatureToServiceArea,
	buildDistrictServiceAreasInStateExcluding,
	buildRegionServiceAreasInCountryExcluding,
	hasRenderableMapCanvas,
	isFiniteScreenNumber,
	readGeoJsonSourceFeatures,
	resolveCountryGeometryFromArea,
	resolveStateGeometryFromArea,
} from './progressiveGeoBoundariesMapCoreGeometry';

export type BoundaryRefinementKind =
	| 'state_to_districts_excluding_child'
	| 'country_to_regions_excluding_child'
	| 'country_to_districts_excluding_child';

export interface BoundaryRefinementRequest {
	kind: BoundaryRefinementKind;
	parentArea: ServiceArea;
	childArea: ServiceArea;
}

export function findWholeStateSelectionCoveringDistrict(
	districtChild: ServiceArea,
	selectedServiceAreas: ServiceArea[],
	getStateLabel: (a: ServiceArea) => string,
	getCountryLabel: (a: ServiceArea) => string
): ServiceArea | null {
	const districtStateLabel = getStateLabel(districtChild);
	const districtCountryLabel = getCountryLabel(districtChild);
	if (!districtStateLabel || !districtCountryLabel) {
		return null;
	}
	for (let areaIndex = 0; areaIndex < selectedServiceAreas.length; areaIndex++) {
		const candidateArea = selectedServiceAreas[areaIndex];
		if (!candidateArea || candidateArea.placeType !== 'region') {
			continue;
		}
		if (
			getStateLabel(candidateArea) === districtStateLabel &&
			getCountryLabel(candidateArea) === districtCountryLabel
		) {
			return candidateArea;
		}
	}
	return null;
}

export function findWholeCountrySelectionCoveringRegion(
	regionChild: ServiceArea,
	selectedServiceAreas: ServiceArea[],
	getCountryLabel: (a: ServiceArea) => string
): ServiceArea | null {
	const regionCountryLabel = getCountryLabel(regionChild);
	if (!regionCountryLabel) {
		return null;
	}
	for (let areaIndex = 0; areaIndex < selectedServiceAreas.length; areaIndex++) {
		const candidateArea = selectedServiceAreas[areaIndex];
		if (!candidateArea || candidateArea.placeType !== 'country') {
			continue;
		}
		if (getCountryLabel(candidateArea) === regionCountryLabel) {
			return candidateArea;
		}
	}
	return null;
}

export function findWholeCountrySelectionCoveringDistrict(
	districtChild: ServiceArea,
	selectedServiceAreas: ServiceArea[],
	getCountryLabel: (a: ServiceArea) => string
): ServiceArea | null {
	const districtCountryLabel = getCountryLabel(districtChild);
	if (!districtCountryLabel) {
		return null;
	}
	for (let areaIndex = 0; areaIndex < selectedServiceAreas.length; areaIndex++) {
		const candidateArea = selectedServiceAreas[areaIndex];
		if (!candidateArea || candidateArea.placeType !== 'country') {
			continue;
		}
		if (getCountryLabel(candidateArea) === districtCountryLabel) {
			return candidateArea;
		}
	}
	return null;
}

/**
 * Line colour for the progressive hover outline: when a whole state or whole country row “covers”
 * the hovered admin feature, use that parent’s selection line colour (not the child admin default).
 * Ignores locality (ADM3) special cases beyond the default locality line colour.
 */
export function resolveBoundaryHoverLineColor(
	clickedShapeTypeUpper: string,
	enrichedBoundary: ServiceArea,
	selectedServiceAreas: ServiceArea[],
	getStateLabel: (a: ServiceArea) => string,
	getCountryLabel: (a: ServiceArea) => string
): string {
	const adminLevelUpper = clickedShapeTypeUpper ? String(clickedShapeTypeUpper).toUpperCase() : '';
	if (adminLevelUpper === 'ADM0') {
		return SEL_COUNTRY_LINE;
	}
	if (adminLevelUpper === 'ADM3') {
		return SEL_LOCALITY_LINE;
	}
	if (adminLevelUpper === 'ADM1') {
		const countryParent = findWholeCountrySelectionCoveringRegion(
			enrichedBoundary,
			selectedServiceAreas,
			getCountryLabel
		);
		if (countryParent) {
			return SEL_COUNTRY_LINE;
		}
		return SEL_REGION_LINE;
	}
	if (adminLevelUpper === 'ADM2') {
		const stateParent = findWholeStateSelectionCoveringDistrict(
			enrichedBoundary,
			selectedServiceAreas,
			getStateLabel,
			getCountryLabel
		);
		if (stateParent) {
			return SEL_REGION_LINE;
		}
		const countryParent = findWholeCountrySelectionCoveringDistrict(
			enrichedBoundary,
			selectedServiceAreas,
			getCountryLabel
		);
		if (countryParent) {
			return SEL_COUNTRY_LINE;
		}
		return SEL_DISTRICT_LINE;
	}
	return SEL_DISTRICT_LINE;
}

/**
 * Coarse selection (whole country or whole state) refined by clicking a contained admin boundary
 * at the granularity implied by the form mode (`region` → state click, `district` → county click).
 */
export function resolveBoundaryRefinementFromMapClick(
	clickedShapeTypeUpper: string,
	formMode: string,
	enrichedChild: ServiceArea,
	selectedServiceAreas: ServiceArea[],
	getStateLabel: (a: ServiceArea) => string,
	getCountryLabel: (a: ServiceArea) => string
): BoundaryRefinementRequest | null {
	const normalizedFormMode = formMode ? String(formMode) : 'district';
	if (normalizedFormMode === 'region' && clickedShapeTypeUpper === 'ADM1') {
		const countryParent = findWholeCountrySelectionCoveringRegion(
			enrichedChild,
			selectedServiceAreas,
			getCountryLabel
		);
		if (countryParent) {
			return {
				kind: 'country_to_regions_excluding_child',
				parentArea: countryParent,
				childArea: enrichedChild,
			};
		}
	}
	if (normalizedFormMode === 'district' && clickedShapeTypeUpper === 'ADM2') {
		const stateParent = findWholeStateSelectionCoveringDistrict(
			enrichedChild,
			selectedServiceAreas,
			getStateLabel,
			getCountryLabel
		);
		if (stateParent) {
			return {
				kind: 'state_to_districts_excluding_child',
				parentArea: stateParent,
				childArea: enrichedChild,
			};
		}
		const countryParent = findWholeCountrySelectionCoveringDistrict(
			enrichedChild,
			selectedServiceAreas,
			getCountryLabel
		);
		if (countryParent) {
			return {
				kind: 'country_to_districts_excluding_child',
				parentArea: countryParent,
				childArea: enrichedChild,
			};
		}
	}
	return null;
}

const CONSOLIDATE_LIST_ALL_EXCLUDE_ID = '__ow_consolidate_list_all__';

function listAllDistrictsInStateGeometry(
	map: any,
	stateArea: ServiceArea,
	adm1SourceId: string,
	adm2SourceId: string
): ServiceArea[] {
	const stateGeom = resolveStateGeometryFromArea(map, stateArea, adm1SourceId);
	if (!stateGeom) {
		return [];
	}
	const adm2Features = readGeoJsonSourceFeatures(map, adm2SourceId);
	return buildDistrictServiceAreasInStateExcluding(
		adm2Features,
		stateGeom,
		CONSOLIDATE_LIST_ALL_EXCLUDE_ID
	);
}

function listAllRegionsInCountryGeometry(
	map: any,
	countryArea: ServiceArea,
	adm0SourceId: string,
	adm1SourceId: string
): ServiceArea[] {
	const countryGeom = resolveCountryGeometryFromArea(map, countryArea, adm0SourceId);
	const adm1Features = readGeoJsonSourceFeatures(map, adm1SourceId);
	return buildRegionServiceAreasInCountryExcluding(
		adm1Features,
		countryGeom,
		CONSOLIDATE_LIST_ALL_EXCLUDE_ID,
		countryArea
	);
}

function boundaryIdSetFromAreas(areasList: ServiceArea[]): Set<string> | null {
	const boundaryShapeIdSet = new Set<string>();
	for (let areaIndex = 0; areaIndex < areasList.length; areaIndex++) {
		const boundaryShapeId = areasList[areaIndex].boundaryShapeId;
		if (
			boundaryShapeId === undefined ||
			boundaryShapeId === null ||
			String(boundaryShapeId) === ''
		) {
			return null;
		}
		boundaryShapeIdSet.add(String(boundaryShapeId));
	}
	return boundaryShapeIdSet;
}

function boundaryIdSetsEqual(selectedIds: Set<string>, expectedAreas: ServiceArea[]): boolean {
	if (expectedAreas.length === 0) {
		return false;
	}
	const expectedSet = boundaryIdSetFromAreas(expectedAreas);
	if (!expectedSet || selectedIds.size !== expectedSet.size) {
		return false;
	}
	for (const id of expectedSet) {
		if (!selectedIds.has(id)) {
			return false;
		}
	}
	return true;
}

function findAdm1ServiceAreaByLabels(
	map: any,
	stateLabel: string,
	countryLabel: string,
	adm1SourceId: string,
	getStateLabel: (a: ServiceArea) => string,
	getCountryLabel: (a: ServiceArea) => string
): ServiceArea | null {
	const adm1Features = readGeoJsonSourceFeatures(map, adm1SourceId);
	// Build ISO → country display-name lookup from ADM0 so we can enrich ADM1
	// feature names (geoboundaries shapeName omits the country).
	const adm0Features = readGeoJsonSourceFeatures(map, SOURCE_ADM0);
	const isoToCountryName: Record<string, string> = {};
	for (let adm0Index = 0; adm0Index < adm0Features.length; adm0Index++) {
		const props = adm0Features[adm0Index] && adm0Features[adm0Index].properties;
		if (!props) continue;
		const iso = props.shapeGroup || props.shapeISO;
		const name = props.shapeName;
		if (iso && name) isoToCountryName[String(iso)] = String(name);
	}
	for (let featureIndex = 0; featureIndex < adm1Features.length; featureIndex++) {
		const adm1Feature = adm1Features[featureIndex];
		const stateOrRegionArea = boundaryFeatureToServiceArea(adm1Feature);
		// Enrich name with country when the raw shapeName lacks it
		if (getCountryLabel(stateOrRegionArea) !== countryLabel) {
			const featureProps = adm1Feature && adm1Feature.properties;
			const featureIso = featureProps
				? featureProps.shapeGroup || featureProps.shapeISO
				: undefined;
			const resolvedCountry = featureIso ? isoToCountryName[String(featureIso)] : undefined;
			if (resolvedCountry) {
				stateOrRegionArea.name = stateOrRegionArea.name + ', ' + resolvedCountry;
			}
		}
		if (
			getStateLabel(stateOrRegionArea) === stateLabel &&
			getCountryLabel(stateOrRegionArea) === countryLabel
		) {
			return stateOrRegionArea;
		}
	}
	return null;
}

function findAdm0ServiceAreaByCountryLabel(
	map: any,
	countryLabel: string,
	adm0SourceId: string,
	getCountryLabel: (a: ServiceArea) => string
): ServiceArea | null {
	const adm0Features = readGeoJsonSourceFeatures(map, adm0SourceId);
	for (let featureIndex = 0; featureIndex < adm0Features.length; featureIndex++) {
		const countryAreaFromFeature = boundaryFeatureToServiceArea(adm0Features[featureIndex]);
		if (getCountryLabel(countryAreaFromFeature) === countryLabel) {
			return countryAreaFromFeature;
		}
	}
	return null;
}

function replaceIndicesWithSingleArea(
	original: ServiceArea[],
	removeIndices: number[],
	newArea: ServiceArea
): ServiceArea[] {
	const removeSet = new Set(removeIndices);
	const insertAt = Math.min.apply(null, removeIndices);
	const next: ServiceArea[] = [];
	for (let rowIndex = 0; rowIndex < original.length; rowIndex++) {
		if (removeSet.has(rowIndex)) {
			if (rowIndex === insertAt) {
				next.push(newArea);
			}
			continue;
		}
		next.push(original[rowIndex]);
	}
	return next;
}

function serviceAreasListFingerprint(list: ServiceArea[]): string {
	const fingerprintParts: string[] = [];
	for (let rowIndex = 0; rowIndex < list.length; rowIndex++) {
		const serviceArea = list[rowIndex];
		fingerprintParts.push(
			(serviceArea.placeType || '') +
				':' +
				String(serviceArea.boundaryShapeId != null ? serviceArea.boundaryShapeId : '') +
				':' +
				(serviceArea.name || '')
		);
	}
	return fingerprintParts.join('\n');
}

function tryConsolidateDistrictPartitionsOnce(
	map: any,
	areas: ServiceArea[],
	getStateLabel: (a: ServiceArea) => string,
	getCountryLabel: (a: ServiceArea) => string
): ServiceArea[] {
	const stateCountryKeyToRowIndices: Record<string, number[]> = {};
	for (let areaIndex = 0; areaIndex < areas.length; areaIndex++) {
		if (areas[areaIndex].placeType !== 'district') {
			continue;
		}
		const stateCountryGroupKey =
			getStateLabel(areas[areaIndex]) + '\x1e' + getCountryLabel(areas[areaIndex]);
		if (!stateCountryKeyToRowIndices[stateCountryGroupKey]) {
			stateCountryKeyToRowIndices[stateCountryGroupKey] = [];
		}
		stateCountryKeyToRowIndices[stateCountryGroupKey].push(areaIndex);
	}
	const partitionKeys = Object.keys(stateCountryKeyToRowIndices);
	for (let partitionIndex = 0; partitionIndex < partitionKeys.length; partitionIndex++) {
		const rowIndicesForPartition = stateCountryKeyToRowIndices[partitionKeys[partitionIndex]];
		if (rowIndicesForPartition.length === 0) {
			continue;
		}
		const sampleDistrict = areas[rowIndicesForPartition[0]];
		const stateLabel = getStateLabel(sampleDistrict);
		const countryLabel = getCountryLabel(sampleDistrict);
		if (!stateLabel || !countryLabel) {
			continue;
		}
		if (
			areas.some(
				(candidateArea) =>
					candidateArea.placeType === 'region' &&
					getStateLabel(candidateArea) === stateLabel &&
					getCountryLabel(candidateArea) === countryLabel
			)
		) {
			continue;
		}
		const stateArea = findAdm1ServiceAreaByLabels(
			map,
			stateLabel,
			countryLabel,
			SOURCE_ADM1,
			getStateLabel,
			getCountryLabel
		);
		if (!stateArea) {
			continue;
		}
		const allDistricts = listAllDistrictsInStateGeometry(map, stateArea, SOURCE_ADM1, SOURCE_ADM2);
		if (allDistricts.length === 0) {
			continue;
		}
		const selectedDistrictRows = rowIndicesForPartition.map((rowIndex) => areas[rowIndex]);
		const selectedIds = boundaryIdSetFromAreas(selectedDistrictRows);
		if (!selectedIds || !boundaryIdSetsEqual(selectedIds, allDistricts)) {
			continue;
		}
		return replaceIndicesWithSingleArea(areas, rowIndicesForPartition, stateArea);
	}
	return areas;
}

function tryConsolidateRegionPartitionsOnce(
	map: any,
	areas: ServiceArea[],
	getCountryLabel: (a: ServiceArea) => string
): ServiceArea[] {
	const countryLabelToRowIndices: Record<string, number[]> = {};
	for (let areaIndex = 0; areaIndex < areas.length; areaIndex++) {
		if (areas[areaIndex].placeType !== 'region') {
			continue;
		}
		const countryLabelKey = getCountryLabel(areas[areaIndex]);
		if (!countryLabelKey) {
			continue;
		}
		if (!countryLabelToRowIndices[countryLabelKey]) {
			countryLabelToRowIndices[countryLabelKey] = [];
		}
		countryLabelToRowIndices[countryLabelKey].push(areaIndex);
	}
	const partitionCountryLabels = Object.keys(countryLabelToRowIndices);
	for (let partitionIndex = 0; partitionIndex < partitionCountryLabels.length; partitionIndex++) {
		const countryLabel = partitionCountryLabels[partitionIndex];
		const rowIndicesForPartition = countryLabelToRowIndices[countryLabel];
		if (rowIndicesForPartition.length === 0) {
			continue;
		}
		if (
			areas.some(
				(candidateArea) =>
					candidateArea.placeType === 'country' && getCountryLabel(candidateArea) === countryLabel
			)
		) {
			continue;
		}
		const countryArea = findAdm0ServiceAreaByCountryLabel(
			map,
			countryLabel,
			SOURCE_ADM0,
			getCountryLabel
		);
		if (!countryArea) {
			continue;
		}
		const allRegions = listAllRegionsInCountryGeometry(map, countryArea, SOURCE_ADM0, SOURCE_ADM1);
		if (allRegions.length === 0) {
			continue;
		}
		const selectedRegionRows = rowIndicesForPartition.map((rowIndex) => areas[rowIndex]);
		const selectedIds = boundaryIdSetFromAreas(selectedRegionRows);
		if (!selectedIds || !boundaryIdSetsEqual(selectedIds, allRegions)) {
			continue;
		}
		return replaceIndicesWithSingleArea(areas, rowIndicesForPartition, countryArea);
	}
	return areas;
}

/**
 * When loaded map GeoJSON shows every county in a state (or every state in a country) is selected,
 * replace those rows with a single whole-state or whole-country `ServiceArea` so the list and
 * map selection highlight match a coarse pick. No-op if boundaries are not loaded or incomplete.
 */
export function consolidateProgressiveServiceAreas(
	map: any,
	areas: ServiceArea[],
	getStateLabel: (a: ServiceArea) => string,
	getCountryLabel: (a: ServiceArea) => string
): ServiceArea[] {
	if (!map || !areas || areas.length === 0) {
		return areas;
	}
	let working = areas.slice();
	for (let consolidationRound = 0; consolidationRound < 12; consolidationRound++) {
		const fingerprintBeforePass = serviceAreasListFingerprint(working);
		let next = tryConsolidateDistrictPartitionsOnce(map, working, getStateLabel, getCountryLabel);
		next = tryConsolidateRegionPartitionsOnce(map, next, getCountryLabel);
		if (serviceAreasListFingerprint(next) === fingerprintBeforePass) {
			break;
		}
		working = next;
	}
	return working;
}

function trailingSegmentFromPlaceName(placeName: string): string {
	const parts = (placeName || '')
		.split(',')
		.map((nameSegment) => nameSegment.trim())
		.filter((trimmedSegment) => !!trimmedSegment);
	return parts.length > 1 ? parts[parts.length - 1] : '';
}

/**
 * For search picks: resolve to the admin boundary that matches the geocoder's own `place_type`
 * first (so "Texas" → state, not a county inside it), and only fall back to the finest-first
 * chain (ADM3 → ADM2 → ADM1 → ADM0) if the matching layer has no hit. Querying one level at a
 * time avoids Mapbox returning a coarser polygon when finer data exists.
 */
function preferredAdminLayerForSearchPlaceType(searchPlaceType: string): string | null {
	const normalizedPlaceType = String(searchPlaceType || '').toLowerCase();
	if (normalizedPlaceType === 'country') {
		return LAYER_ADM0_FILL;
	}
	if (normalizedPlaceType === 'region' || normalizedPlaceType === 'state') {
		return LAYER_ADM1_FILL;
	}
	if (normalizedPlaceType === 'district') {
		return LAYER_ADM2_FILL;
	}
	if (normalizedPlaceType === 'place' || normalizedPlaceType === 'locality') {
		return LAYER_ADM3_FILL;
	}
	return null;
}

export function resolveSearchAreaToFinestBoundary(map: any, area: ServiceArea): ServiceArea {
	if (!map || !area || !area.center || area.center.length < 2) {
		return area;
	}
	if (area.boundaryShapeId) {
		return area;
	}
	const searchCenterLng = area.center[0];
	const searchCenterLat = area.center[1];
	let projectedScreenPoint: any;
	try {
		projectedScreenPoint = map.project({ lng: searchCenterLng, lat: searchCenterLat });
	} catch (_err) {
		return area;
	}
	if (
		!projectedScreenPoint ||
		!isFiniteScreenNumber(projectedScreenPoint.x) ||
		!isFiniteScreenNumber(projectedScreenPoint.y)
	) {
		return area;
	}
	if (!hasRenderableMapCanvas(map)) {
		return area;
	}

	function tryResolveBoundaryAtLayer(layerId: string): ServiceArea | null {
		if (!map.getLayer(layerId)) {
			return null;
		}
		let renderedFeaturesAtPoint: any[];
		try {
			renderedFeaturesAtPoint = map.queryRenderedFeatures(projectedScreenPoint, {
				layers: [layerId],
			});
		} catch (_err) {
			return null;
		}
		if (!renderedFeaturesAtPoint || renderedFeaturesAtPoint.length === 0) {
			return null;
		}
		// `queryRenderedFeatures` returns geometry clipped to the tile the query point fell in, not
		// the full polygon. That's fine for identifying the hit (shapeID is intact), but the clipped
		// ring is roughly rectangular — using it as the preview geometry draws a gray bounding box
		// over the state. Swap in the unclipped feature from the source using shapeID so the preview
		// outline matches the real state/county/country polygon.
		const tileClippedHitFeature = renderedFeaturesAtPoint[0];
		const tileClippedProps =
			tileClippedHitFeature && tileClippedHitFeature.properties
				? tileClippedHitFeature.properties
				: {};
		const hitShapeId =
			tileClippedProps.shapeID != null ? String(tileClippedProps.shapeID) : '';
		const hitSourceId =
			tileClippedHitFeature && tileClippedHitFeature.source
				? String(tileClippedHitFeature.source)
				: '';
		let effectiveFeatureForServiceArea: any = tileClippedHitFeature;
		if (hitShapeId && hitSourceId) {
			const unclippedSourceFeatures = readGeoJsonSourceFeatures(map, hitSourceId);
			for (
				let unclippedFeatureIndex = 0;
				unclippedFeatureIndex < unclippedSourceFeatures.length;
				unclippedFeatureIndex++
			) {
				const candidate = unclippedSourceFeatures[unclippedFeatureIndex];
				if (
					candidate &&
					candidate.properties &&
					String(candidate.properties.shapeID) === hitShapeId
				) {
					effectiveFeatureForServiceArea = candidate;
					break;
				}
			}
		}
		const boundaryFromHit = boundaryFeatureToServiceArea(effectiveFeatureForServiceArea);
		if (!boundaryFromHit.boundaryShapeId) {
			return null;
		}
		let mergedDisplayName = area.name;
		if (boundaryFromHit.placeType === 'district') {
			mergedDisplayName = area.name;
		} else if (boundaryFromHit.placeType === 'region') {
			const countryGuessFromSearchName = trailingSegmentFromPlaceName(area.name);
			mergedDisplayName =
				countryGuessFromSearchName && countryGuessFromSearchName !== boundaryFromHit.name
					? boundaryFromHit.name + ', ' + countryGuessFromSearchName
					: boundaryFromHit.name;
		} else if (boundaryFromHit.placeType === 'country') {
			mergedDisplayName = boundaryFromHit.name;
		} else if (boundaryFromHit.placeType === 'place') {
			mergedDisplayName = boundaryFromHit.name;
		}
		return {
			...area,
			name: mergedDisplayName,
			boundaryShapeId: boundaryFromHit.boundaryShapeId,
			boundaryShapeGroup: boundaryFromHit.boundaryShapeGroup || undefined,
			placeType: boundaryFromHit.placeType,
			bbox: boundaryFromHit.bbox != null ? boundaryFromHit.bbox : area.bbox,
			center: area.center,
			boundaries: boundaryFromHit.boundaries || undefined,
		};
	}

	// Search intent wins: if Mapbox told us this is a `region`/`state`/`country`/`district`/`place`,
	// resolve to that admin level first so searching "Texas" doesn't land on a county at Texas's
	// geographic center just because ADM2 happened to render beneath the geocode point at zoom 8+.
	const preferredLayerId = preferredAdminLayerForSearchPlaceType(area.placeType || '');
	if (preferredLayerId) {
		const preferredBoundary = tryResolveBoundaryAtLayer(preferredLayerId);
		if (preferredBoundary) {
			return preferredBoundary;
		}
	}
	// Fallback: finest-first chain for ambiguous / unknown place types (postcode, poi, no place_type).
	const localityBoundary = tryResolveBoundaryAtLayer(LAYER_ADM3_FILL);
	if (localityBoundary) {
		return localityBoundary;
	}
	const districtBoundary = tryResolveBoundaryAtLayer(LAYER_ADM2_FILL);
	if (districtBoundary) {
		return districtBoundary;
	}
	const regionBoundary = tryResolveBoundaryAtLayer(LAYER_ADM1_FILL);
	if (regionBoundary) {
		return regionBoundary;
	}
	const countryBoundary = tryResolveBoundaryAtLayer(LAYER_ADM0_FILL);
	if (countryBoundary) {
		return countryBoundary;
	}
	return area;
}

export function normalizeFeatures(inputFeatureCollection: any): any[] {
	if (!inputFeatureCollection || !inputFeatureCollection.features) {
		return [];
	}
	const normalizedFeatureList: any[] = [];
	for (
		let featureIndex = 0;
		featureIndex < inputFeatureCollection.features.length;
		featureIndex++
	) {
		const rawFeature = inputFeatureCollection.features[featureIndex];
		if (!rawFeature || !rawFeature.properties) {
			continue;
		}
		const shapeIdRaw = rawFeature.properties.shapeID;
		if (shapeIdRaw !== undefined && shapeIdRaw !== null) {
			const shapeIdString = String(shapeIdRaw);
			rawFeature.properties.shapeID = shapeIdString;
			rawFeature.id = shapeIdString;
		}
		normalizedFeatureList.push(rawFeature);
	}
	return normalizedFeatureList;
}

/** geoBoundaries files often include legacy `crs`; Mapbox expects RFC 7946 FC + stable feature ids for filters. */
export function toMapboxFeatureCollection(inputFeatureCollection: any): any {
	const normalizedFeatures = normalizeFeatures(inputFeatureCollection);
	return { type: 'FeatureCollection', features: normalizedFeatures };
}

export function mergeDedupeAdm0(existing: any[], incoming: any[]): any[] {
	const getAdm0FeatureDedupeKey = (feature: any) => {
		const props = feature.properties || {};
		return (props.shapeGroup || '') + '|' + (props.shapeType || 'ADM0');
	};
	const seenDedupeKeys: Record<string, boolean> = {};
	const mergedAdm0Features: any[] = [];
	for (let existingIndex = 0; existingIndex < existing.length; existingIndex++) {
		const existingDedupeKey = getAdm0FeatureDedupeKey(existing[existingIndex]);
		if (!seenDedupeKeys[existingDedupeKey]) {
			seenDedupeKeys[existingDedupeKey] = true;
			mergedAdm0Features.push(existing[existingIndex]);
		}
	}
	for (let incomingIndex = 0; incomingIndex < incoming.length; incomingIndex++) {
		const incomingDedupeKey = getAdm0FeatureDedupeKey(incoming[incomingIndex]);
		if (!seenDedupeKeys[incomingDedupeKey]) {
			seenDedupeKeys[incomingDedupeKey] = true;
			mergedAdm0Features.push(incoming[incomingIndex]);
		}
	}
	return mergedAdm0Features;
}

export async function runPool<T>(
	items: T[],
	limit: number,
	worker: (item: T) => Promise<void>
): Promise<void> {
	let nextWorkerItemIndex = 0;
	const runners = new Array(Math.min(limit, Math.max(items.length, 1))).fill(0).map(() =>
		(async () => {
			for (;;) {
				const claimedItemIndex = nextWorkerItemIndex++;
				if (claimedItemIndex >= items.length) {
					return;
				}
				await worker(items[claimedItemIndex]);
			}
		})()
	);
	await Promise.all(runners);
}

export type ProgressiveMapInteractionMode = 'boundaries' | 'pin_drop';

/**
 * True when the current tab’s selection list already includes this boundary (same shape id and
 * matching place type for the tab). Does not treat “whole state selected” as the hovered county selected.
 */
export function isBoundaryExplicitlySelectedForFormMode(
	formMode: string,
	hoveredBoundaryShapeId: string,
	selectedAreas: ServiceArea[]
): boolean {
	if (!hoveredBoundaryShapeId || !selectedAreas || selectedAreas.length === 0) {
		return false;
	}
	const hoveredShapeIdNormalized = String(hoveredBoundaryShapeId);
	const activeTabMode = formMode ? String(formMode) : 'district';
	for (let selectedIndex = 0; selectedIndex < selectedAreas.length; selectedIndex++) {
		const candidate = selectedAreas[selectedIndex];
		if (!candidate || candidate.boundaryShapeId == null) {
			continue;
		}
		if (String(candidate.boundaryShapeId) !== hoveredShapeIdNormalized) {
			continue;
		}
		const candidatePlaceType = candidate.placeType;
		if (activeTabMode === 'country' && candidatePlaceType === 'country') {
			return true;
		}
		if (
			activeTabMode === 'region' &&
			(candidatePlaceType === 'region' || candidatePlaceType === 'state')
		) {
			return true;
		}
		if (activeTabMode === 'district' && candidatePlaceType === 'district') {
			return true;
		}
		if (activeTabMode === 'place' && candidatePlaceType === 'place') {
			return true;
		}
	}
	return false;
}

/**
 * Tooltip “Deselect” when a coarser selection already implies this boundary (e.g. whole USA selected
 * and user hovers a state on the State tab, or whole state / country and user hovers a county).
 * Uses the same parent/child rules as refinement and hover line colour.
 */
export function isHoverCoveredByCoarserSelection(
	formMode: string,
	shapeTypeUpper: string,
	enrichedHoveredArea: ServiceArea,
	selectedAreas: ServiceArea[],
	getStateLabel: (a: ServiceArea) => string,
	getCountryLabel: (a: ServiceArea) => string
): boolean {
	if (!selectedAreas || selectedAreas.length === 0) {
		return false;
	}
	const mode = formMode ? String(formMode) : 'district';
	const admin = shapeTypeUpper ? String(shapeTypeUpper).toUpperCase() : '';
	if (mode === 'region' && admin === 'ADM1') {
		return (
			findWholeCountrySelectionCoveringRegion(
				enrichedHoveredArea,
				selectedAreas,
				getCountryLabel
			) != null
		);
	}
	if (mode === 'district' && admin === 'ADM2') {
		if (
			findWholeStateSelectionCoveringDistrict(
				enrichedHoveredArea,
				selectedAreas,
				getStateLabel,
				getCountryLabel
			) != null
		) {
			return true;
		}
		return (
			findWholeCountrySelectionCoveringDistrict(
				enrichedHoveredArea,
				selectedAreas,
				getCountryLabel
			) != null
		);
	}
	return false;
}

export function pickFinestSubAdminGeocodeFeature(features: any[]): any | null {
	if (!features || features.length === 0) {
		return null;
	}
	for (let featureIndex = 0; featureIndex < features.length; featureIndex++) {
		const geocodeFeature = features[featureIndex];
		const primaryPlaceType = geocodeFeature.place_type && geocodeFeature.place_type[0];
		if (
			primaryPlaceType &&
			primaryPlaceType !== 'country' &&
			primaryPlaceType !== 'region' &&
			primaryPlaceType !== 'district'
		) {
			return geocodeFeature;
		}
	}
	return features[0];
}

export interface ProgressiveGeoBoundariesAttachOptions {
	/** Latest click handler (avoids stale closure from map init). */
	getOnSelect?: () => ((area: ServiceArea) => void) | undefined;
	/** When `pin_drop`, map clicks add a precise coordinate instead of selecting admin boundaries. */
	getInteractionMode?: () => ProgressiveMapInteractionMode;
	/** Called with click lng/lat when interaction mode is `pin_drop`. */
	getOnPinSelect?: () => ((lngLat: { lng: number; lat: number }) => void) | undefined;
	/**
	 * Form mode: `country` | `region` | `district` | `place` | `pin`.
	 * Limits which boundary layers participate in hover/click (finer levels first within the allowed set).
	 * Default when omitted: `district` (legacy progressive behavior).
	 */
	getSelectionFormMode?: () => string;
	/** Called when boundary data starts or finishes loading. `level` is 'ADM0' | 'ADM1' | 'ADM2'. */
	onBoundaryLoadingChange?: (loading: boolean, level: string) => void;
	/** When false, skip hover highlights and click handlers (read-only preview). Default true. */
	interactive?: boolean;
	/** Current selections — used to detect “county inside selected state” for subtract UX. */
	getServiceAreas?: () => ServiceArea[];
	/** Same semantics as `getStateFromArea` in the service-area form (state label for matching). */
	getStateLabelFromArea?: (area: ServiceArea) => string;
	/** Same semantics as `getCountryFromArea` in the service-area form. */
	getCountryLabelFromArea?: (area: ServiceArea) => string;
	/**
	 * Replace a whole-country or whole-state selection with explicit child admin areas, excluding the
	 * clicked boundary (see `resolveBoundaryRefinementFromMapClick`).
	 */
	getOnApplyBoundaryRefinement?: () => ((request: BoundaryRefinementRequest) => void) | undefined;
}
